import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import CustomHorizontalTabs from '../../CustomHorizontalTabs/CustomHorizontalTabs';
import { useParentDashboardContextContext } from '../../../context/ParentDashboardContext';
import { useApi } from '../../../context/ApiProvider';
import { ParentSupportData } from '../../../shared/type/support.type';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';
import IconComponent from '../../IconComponent/IconComponent';
import { useAlertContext } from '../../../context/AlertProvider';

export const Contact = () => {
  const { t } = useSafeTranslation();
  const { parentDashboardContext, setBasketStatus } = useParentDashboardContextContext();
  const { apiParentSupportController } = useApi();

  const [chosenTenantId, setChosenTenantId] = useState<number | null>(null);
  const [supportData, setSupportData] = useState<ParentSupportData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchTenantContactData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<ParentSupportData> = await apiParentSupportController('').get(
        '',
        {
          params: {
            tenantId: chosenTenantId
          }
        }
      );
      setSupportData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [chosenTenantId]);

  useEffect(() => {
    if (parentDashboardContext) {
      setChosenTenantId(parentDashboardContext.tenants[0].tenantId);
    }
  }, [parentDashboardContext]);

  useEffect(() => {
    if (chosenTenantId) {
      fetchTenantContactData();
    }
  }, [chosenTenantId]);

  useEffect(() => {
    setBasketStatus(() => ({ show: false, forceShow: false }));
  }, []);

  return (
    <section className="h-screen flex flex-col bg-grayLight-50 overflow-y-auto pb-20 md:pb-0">
      <div className="pt-spacing-3xl pr-spacing-3xl pb-spacing-xl pl-spacing-3xl">
        <h1 className="p-0 m-0 font-semibold text-grayLight-900" style={{ fontSize: '30px' }}>
          {t('parent_support.title')}
        </h1>
      </div>
      <div className="flex flex-col pr-spacing-4xl pl-spacing-3xl pb-spacing-xl gap-spacing-xl">
        <CustomHorizontalTabs
          onChange={(value) => {
            setChosenTenantId(value);
          }}
          data={
            parentDashboardContext
              ? parentDashboardContext.tenants.map((tenant) => ({
                  label: tenant.tenantName,
                  value: tenant.tenantId
                }))
              : null
          }
        />
      </div>
      <div className="flex flex-col gap-spacing-xl pt-spacing-md pr-spacing-xl pb-spacing-md pl-spacing-xl">
        {isLoading || !supportData ? (
          <LoadingSpinner state={isLoading}>
            <>{t('parent_support.loading')}</>
          </LoadingSpinner>
        ) : (
          <>
            <div>
              <h2 className="text-md font-bold">{t('parent_support.contact_for_orders')}</h2>

              <ContactInfoRow label={t('parent_support.email')} value={supportData.supportEmail} />

              <ContactInfoRow label={t('parent_support.phone')} value={supportData.supportPhone} />
            </div>
            <div className="mt-4">
              <h2 className="text-md font-bold">{t('parent_support.contact_for_support')}</h2>

              <ContactInfoRow label={t('parent_support.email')} value="kontakt@jemwszkole.pl" />

              <ContactInfoRow label={t('parent_support.phone')} value="+48 535 818 986" />
            </div>
            <div className="mt-4">
              <h2 className="text-md font-bold">{t('parent_support.documents')}</h2>

              <ContactInfoLink label={t('parent_support.statute')} value={supportData.mainStatuteUrl}/>

              {supportData.tenantStatuteUrl && (
                <ContactInfoLink
                  label={t('parent_support.tenant_statute')}
                  value={supportData.tenantStatuteUrl}
                />
              )}
              {!supportData.tenantStatuteUrl && (
                <ContactInfoLink label={t('parent_support.tenant_statute')} value="" />
              )}

              <ContactInfoLink label={t('parent_support.privacy_policy')} value={supportData.privacyPolicyUrl} />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

interface ContactInfoRowProps {
  label: string;
  value: string;
}

const ContactInfoRow: React.FC<ContactInfoRowProps> = ({ label, value }) => {
  const { t } = useSafeTranslation();
  const { setShow: setShowAlert, setAlertProperties } = useAlertContext();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);

      setAlertProperties({
        timeout: 1000,
        status: 'info',
        title: t('parent_support.copied')
      });
      setShowAlert(true);
      setCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      <div className="border-t border-gray-200 mb-2" />
      <div className="flex items-center py-5 gap-2">
        <p className="w-1/4 text-sm text-gray-700 font-semibold">{label}</p>
        <div className="flex-1 flex items-center">
          <p className="text-sm text-gray-900">{value}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleCopy()}
              className="cursor-pointer p-2 hover:bg-gray-100 rounded"
              title="Kopiuj do schowka">
              <IconComponent iconName="copy" />
            </button>
            {copied && <span className="text-xs text-green-500">Skopiowano!</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactInfoLink: React.FC<ContactInfoRowProps> = ({ label, value }) => {

  return (
    <div>
      <div className="border-t border-gray-200 mb-2" />
      <div className="flex items-center py-5 gap-2">
        <p className="w-1/4 text-sm text-gray-700 font-semibold">{label}</p>
        <div className="flex-1 flex items-center">
          {value &&<p className="text-sm underline text-gray-900"><a href={value} target="_blank">{label}</a></p>}
          {!value &&<p className="text-sm text-gray-900">BRAK</p>}
        </div>
      </div>
    </div>
  );
};
